// src/pages/gallery-profile-options/artists-management/virtual-list/artist-mgmt-card/artist-details/layout/ArtistDetailsLayout.jsx
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import ShareIcon from '@/assets/all-icons/share-icon';
import useGoBack      from '@/hooks/useGoBack';
import GalleryService from '@/services/gallery.service';
import BackArrowIcon  from '@/shared-components/icons/components/public/BackArrowIcon';

import './styles/AdminArtistDetails.css';

export default function ArtistDetailsLayout() {
  const location      = useLocation();
  const initialArtist = location.state?.artist ?? null;
  const [artist, setArtist] = useState(initialArtist);
  const claimed       = artist?.flags?.claimed;
  const initialPerms  = location.state?.permissions;

  const [permsDoc, setPermsDoc] = useState(initialPerms);
  const [sending, setSending]   = useState(false);

  useEffect(() => {
    if (
      location.state?.artist &&
      (!artist || location.state.artist._id !== artist._id)
    ) {
      setArtist(location.state.artist);
    }
  }, [artist, location.state]);

  // ---------- Toast (popup temporal) ----------
  const [toast, setToast] = useState({ open: false, message: '', tone: 'info' });
  const showToast = (message, tone = 'info', ms = 2600) => {
    setToast({ open: true, message, tone });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast({ open: false, message: '', tone }), ms);
  };

  const INVITE_COOLDOWN_MS = 5 * 60 * 1000;

  // ---------- Cálculos para el botón ----------
  let inviteDisabled = false;
  if (permsDoc?.invitation_to_join?.sent) {
    const createdAtMs = new Date(permsDoc.invitation_to_join.createdAt).getTime();
    if (Number.isFinite(createdAtMs)) {
      inviteDisabled = (Date.now() - createdAtMs) < INVITE_COOLDOWN_MS;
    }
  }

  const BLOCKED_EMAIL_SNIPPET = 'artist.suarte.art';
  const artistEmail = String(artist?.artist_email ?? '').trim().toLowerCase();
  const hasBlockedEmail = artistEmail.includes(BLOCKED_EMAIL_SNIPPET);

  const missingEmail = !artistEmail;
  const needsEmail   = missingEmail || hasBlockedEmail;

  const hasWorkOfArtist = (artist?.countWorkOfArtist ?? 0) > 0;

  // Mostrar botón solo si NO está claimed y SÍ tiene al menos un "Work of Artist"
  const showInviteButton = !claimed && hasWorkOfArtist;

  const isDisabled = sending || inviteDisabled || needsEmail;

  /* ---------- handlers ---------- */
  const goBackHandler = useGoBack('/my-artists');

  const inviteHandler = async () => {
    if (!artist) return;

    if (isDisabled) {
      // Motivo del bloqueo → toast
      if (needsEmail) {
        const msg = missingEmail
          ? 'Please add the artist’s email first to enable invitations.'
          : 'Please use a valid artist email (not artist.suarte.art) to enable invitations.';
        showToast(msg, 'warning');
      } else if (inviteDisabled) {
        showToast('An invitation was recently sent. You can send another one after 2 minutes.', 'warning');
      } else if (sending) {
        showToast('Sending invitation…', 'info');
      }
      return;
    }

    setSending(true);
    const { response, data } = await GalleryService.sendInviteToArtist(artist._id, artistEmail);

    if (response.ok) {
      setPermsDoc(data);
      showToast(`Invitation sent to ${artist.artist_name}!`, 'success');
    } else {
      showToast('Could not send invitation. Please try again.', 'error');
    }
    setSending(false);
  };

  /* ---------- texto, tooltip y clases del botón ---------- */
  const btnText = sending
    ? 'Sending…'
    : inviteDisabled
      ? 'Invited (2 min cooldown)'
      : 'Invite to join Suarte';

  const btnTitle = isDisabled
    ? (needsEmail
      ? (missingEmail
        ? 'Please add the artist’s email first to enable invitations.'
        : 'Please use a valid artist email (not artist.suarte.art) to enable invitations.')
      : 'An invitation was recently sent. You can send another one after 2 minutes.')
    : '';

  const btnClasses =
    'artist-details-layout__share' + (isDisabled ? ' disabled' : '');

  // NOTA: si tu CSS pone pointer-events:none en .disabled, lo anulamos para poder mostrar el toast al click.
  const disabledStyleOverride = isDisabled ? { pointerEvents: 'auto', cursor: 'not-allowed' } : undefined;

  return (
    <div className="inventory-layout__page">
      {/* --- Toast (popup) --- */}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 9999,
          transition: 'opacity 180ms ease',
          opacity: toast.open ? 1 : 0,
          pointerEvents: 'none',
        }}
      >
        {toast.open && (
          <div
            style={{
              minWidth: 260,
              maxWidth: 420,
              padding: '12px 14px',
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              background: toast.tone === 'success'
                ? '#e8fff1'
                : toast.tone === 'warning'
                  ? '#fff8e6'
                  : toast.tone === 'error'
                    ? '#ffecec'
                    : '#f4f4f5',
              color: '#111827',
              fontFamily: 'Satoshi, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
              fontSize: 14,
              lineHeight: 1.35,
              border: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            {toast.message}
          </div>
        )}
      </div>

      <div className="inventory-layout__wrapper">
        <div className="artist-layout__header-outter">
          <header className="artist-details-layout__header">
            {/* Back + Title */}
            <BackArrowIcon
              className="inventory-layout__back-button"
              onClick={goBackHandler}
            />
            <span className="artist-details-layout__header-text lt-s">
              {artist?.artist_name ?? 'Artist Details'}
            </span>

            {showInviteButton && (
              <div
                className={btnClasses}
                onClick={inviteHandler}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                aria-disabled={isDisabled}
                title={btnTitle}
                style={disabledStyleOverride}
              >
                <ShareIcon className="artist-details-layout__share-icon" />
                <span className="artist-details-layout__share-text">{btnText}</span>
              </div>
            )}
          </header>
        </div>

        <div className="inventory-layout__content">
          <Outlet context={{ artist, setArtist, permsDoc, setPermsDoc }} />
        </div>
      </div>
    </div>
  );
}
